import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      nav: {
        home: "Home",
        about: "About",
        cases: "Cases",
        archive: "Archive",
        contact: "Contribute",
        contributors: "Contributors",
        evidence: "Evidence",
      },
      common: {
        loading: "Loading...",
        readMore: "Read More",
        viewMore: "View More",
        backToTop: "Back to Top",
        previous: "Previous",
        next: "Next",
      },
      home: {
        welcome: "Welcome to Cultural Ecologies of Memory",
      },
      contact: {
        sendMessage: "Send Message",
        yourName: "Your Name",
        yourEmail: "Your Email",
        message: "Message",
      },
      archiveDetail: {
        title: "Title",
        description: "Description",
        annotation: "Annotation",
        author: "Author",
        subject: "Subject",
        publishedOnCem: "Published on CEM on",
        keywords: "Keywords",
        location: "Location",
        interviewee: "Interviewee",
        country: "Country",
        city: "City",
        collaborators: "Collaborators",
        socialActors: "Social Actors",
        dublinCoreType: "Dublin Core Type",
        category: "Category",
        documentType: "Document Type",
        language: "Language",
        rights: "Rights",
        rightsAnnotation: "Rights annotation",
        useRestriction: "Use Restriction",
        distributionRestriction: "Distribution Restriction",
        publisher: "Publisher",
        publicationDate: "Publication Date",
        format: "Format",
        coverage: "Coverage",
        numberOfPages: "Number of pages",
        dimensions: "Dimensions",
        collectionDate: "Collection Date",
        source: "Source",
        fileType: "File type",
        citation: "Citation",
        resources: "Resources",
        noDate: "No date",
      },
      evidence: {
        title: "Domain evidence browser",
        searchBy: "Explore the evidence:",
        domain: "Domain",
        term: "Term",
        pickDomain: "Pick a domain first...",
        intro:
          "The methodology's word sets, as an instrument: pick a domain, then a term, and read the text surrounding every occurrence — source and archive links included. Boilerplate was cut before indexing, so chrome never matches. By default an occurrence shows only when it shares a passage with a human-rights-activism anchor — the counting rule's evidence view; the toggle below reveals the rest. The census JSON remains the counting authority.",
        collection: "Collection",
        loadingTerms: "loading terms…",
        loading: "loading…",
        entireDomain: "entire domain",
        alsoMentioning: "also mentioning",
        anyPage: "— any page —",
        listings: "include listings/directories",
        outsidePassages: "include occurrences outside activism passages",
        pagesMention: "pages mention",
        and: "and",
        showing: "showing",
        captures: "captures",
        capture: "original as crawled",
        source: "source page",
        fullText: "full text",
        previous: "Previous",
        next: "Next",
        unavailable: "The evidence service is not available.",
        modeSearch: "Search",
        modeNetwork: "Network",
        graphTitle: "Evidence network",
        graphSubtitle: "How the evidence connects",
        graphExplainer:
          "Each circle is a term from the coding lexicon, sized by the number "
          + "of archived pages that mention it. Lines join terms that appear "
          + "on the same pages. Hover to see a term's neighborhood; click a "
          + "term — or a line between two terms — to read the excerpts behind it.",
        graphHintRadial:
          "hover a term to isolate its relationships · click terms or ribbons",
        clearSelection: "Clear selection",
        close: "Close",
        bothTerms: "both terms",
        loadMore: "Load more",
        expandExcerpts: "Expand excerpts to full view",
        backToGraph: "Back to graph view",
        graphDesktopOnly: "The network view is designed for larger screens.",
        useSearch: "Use the search view",
        exploreNetwork: "explore in the evidence network",
        treemapMetric:
          "archived pages mentioning each format, across all collections",
        allInstancesShown: "all instances shown",
        findShiftTitle: "From commemoration toward redress",
        findShiftLede: "Share of the collection's pages using each term, {{early}} against {{late}}.",
        findShiftGrowing: "Growing: accountability and repair",
        findShiftReceding: "Receding: commemoration and disappearance",
        findShiftChange: "Change",
        findShiftCaption: "Change in percentage points · based on {{earlyPages}} and {{latePages}} pages.",
        findTrendsTitle: "The language institutionalizes over time",
        findTrendsLede:
          "Share of each crawl year's pages using a term. The clearest "
          + "single signal: the corpus shifts from “victim” toward "
          + "“survivor”.",
        findRatio: "survivor : victim ratio",
        findRising: "Rising",
        findFalling: "Falling",
        findKeynessTitle: "Two collections, two languages of memory",
        findKeynessLede:
          "Dunning log-likelihood (G²): the terms most statistically "
          + "distinctive of each collection.",
        findDistinctiveOf: "distinctive of",
        findSourcesTitle: "Some memories stand on a single source",
        findSourcesLede:
          "Share of a term's pages held by its single largest source "
          + "domain. Terms carried by one archived site are the corpus's "
          + "most fragile memories.",
        findEffDomains: "eff. domains",
        findCatShares:
          "share of each year's pages carrying at least one term of the "
          + "category",
        findDiscoursesTitle: "{{count}} stable discourses across the categories",
        findPlaces: "Places",
        findLooser: "looser cluster",
        findPeripheral: "loosely attached",
        findPassages: "passages",
        findOneSite: "from one site",
        findDiscoursesLede:
          "Communities detected in the evidence network when "
          + "relationships are weighted by statistical association "
          + "(passage-level PMI), kept only where they recur across a "
          + "few hundred resampled runs; a looser cluster is one whose terms "
          + "group together but only a few hold together reliably. Places "
          + "are not part of the network: the countries listed are those "
          + "whose passages over-represent a discourse, shown with their "
          + "number of passages and flagged when most come from one "
          + "archived site.",
        findTiesTitle: "Strongest ties",
        findTiesLede:
          "The pairs sharing passages far beyond chance (PMI) — precise "
          + "pairings, not the biggest hubs.",
        findTiesShared: "shared passages",
        findTerms: "terms",
        lexSpine: "the central set",
        lexSpineNote:
          "Pages enter the corpus through a list of anchor terms in "
          + "sentence-shaped prose, and are counted only when a validated "
          + "classifier judges them to concern the legacies of past "
          + "violations — the subject of this set. Every other category is "
          + "measured within that context.",
        lexMeasured: "measured within the transitional justice context",
        unlinkedNote:
          "{{count}} terms in this collection appear on too few pages to "
          + "form a relationship (a link needs at least 3 shared pages) and "
          + "are not drawn:",
        noRelationships:
          "No relationships are recorded for this term in this collection: "
          + "no other lexicon term appears on the same pages above the "
          + "display threshold.",
      },
    },
  },
  es: {
    translation: {
      nav: {
        home: "Inicio",
        about: "Acerca de",
        cases: "Casos",
        archive: "Archivo",
        contact: "Colaborar",
        contributors: "Colaboradores",
        evidence: "Evidencia",
      },
      common: {
        loading: "Cargando...",
        readMore: "Leer Más",
        viewMore: "Ver Más",
        backToTop: "Volver Arriba",
        previous: "Anterior",
        next: "Siguiente",
      },
      home: {
        welcome: "Bienvenido a Ecologías Culturales de la Memoria",
      },
      contact: {
        sendMessage: "Enviar Mensaje",
        yourName: "Tu Nombre",
        yourEmail: "Tu Email",
        message: "Mensaje",
      },
      archiveDetail: {
        title: "Título",
        description: "Descripción",
        annotation: "Anotación",
        author: "Autor",
        subject: "Tema",
        publishedOnCem: "Publicado en CEM el",
        keywords: "Palabras clave",
        location: "Ubicación",
        interviewee: "Entrevistado/a",
        country: "País",
        city: "Ciudad",
        collaborators: "Colaboradores",
        socialActors: "Actores sociales",
        dublinCoreType: "Tipo Dublin Core",
        category: "Categoría",
        documentType: "Tipo de documento",
        language: "Idioma",
        rights: "Derechos",
        rightsAnnotation: "Anotación de derechos",
        useRestriction: "Restricción de uso",
        distributionRestriction: "Restricción de distribución",
        publisher: "Editorial",
        publicationDate: "Fecha de publicación",
        format: "Formato",
        coverage: "Cobertura",
        numberOfPages: "Número de páginas",
        dimensions: "Dimensiones",
        collectionDate: "Fecha de recolección",
        source: "Fuente",
        fileType: "Tipo de archivo",
        citation: "Cita",
        resources: "Recursos",
        noDate: "Sin fecha",
      },
      evidence: {
        title: "Explorador de evidencia por dominio",
        searchBy: "Explore la evidencia:",
        domain: "Dominio",
        term: "Término",
        pickDomain: "Elija primero un dominio...",
        intro:
          "Los conjuntos de palabras de la metodología, como instrumento: elija un dominio y luego un término, y lea el texto que rodea cada aparición — con enlaces a la fuente y al archivo. La plantilla repetida se recortó antes de indexar. Por defecto una aparición se muestra solo cuando comparte un pasaje con un ancla de activismo de derechos humanos — la vista de evidencia de la regla de conteo; el interruptor de abajo revela el resto. El censo JSON sigue siendo la autoridad de conteo.",
        collection: "Colección",
        loadingTerms: "cargando términos…",
        loading: "cargando…",
        entireDomain: "dominio completo",
        alsoMentioning: "que también mencione",
        anyPage: "— cualquier página —",
        listings: "incluir listados/directorios",
        outsidePassages: "incluir apariciones fuera de pasajes de activismo",
        pagesMention: "páginas mencionan",
        and: "y",
        showing: "mostrando",
        captures: "capturas",
        capture: "original archivado",
        source: "página fuente",
        fullText: "texto completo",
        previous: "Anterior",
        next: "Siguiente",
        unavailable: "El servicio de evidencia no está disponible.",
        modeSearch: "Búsqueda",
        modeNetwork: "Red",
        graphTitle: "Red de evidencia",
        graphSubtitle: "Cómo se conecta la evidencia",
        graphExplainer:
          "Cada círculo es un término del léxico de codificación, con tamaño "
          + "según el número de páginas archivadas que lo mencionan. Las líneas "
          + "unen términos que aparecen en las mismas páginas. Pase el cursor "
          + "para ver el vecindario de un término; haga clic en un término — o "
          + "en una línea entre dos términos — para leer los extractos detrás.",
        graphHintRadial:
          "pase el cursor sobre un término para aislar sus relaciones · haga "
          + "clic en términos o líneas",
        clearSelection: "Borrar selección",
        close: "Cerrar",
        bothTerms: "ambos términos",
        loadMore: "Cargar más",
        expandExcerpts: "Expandir extractos a vista completa",
        backToGraph: "Volver a la red",
        graphDesktopOnly: "La vista de red está diseñada para pantallas grandes.",
        useSearch: "Usar la vista de búsqueda",
        exploreNetwork: "explorar en la red de evidencia",
        treemapMetric:
          "páginas archivadas que mencionan cada formato, en todas las colecciones",
        allInstancesShown: "todas las instancias mostradas",
        findShiftTitle: "De la conmemoración a la reparación",
        findShiftLede: "Proporción de las páginas de la colección que usan cada término, {{early}} frente a {{late}}.",
        findShiftGrowing: "En aumento: rendición de cuentas y reparación",
        findShiftReceding: "En retroceso: conmemoración y desaparición",
        findShiftChange: "Cambio",
        findShiftCaption: "Cambio en puntos porcentuales · sobre {{earlyPages}} y {{latePages}} páginas.",
        findTrendsTitle: "El lenguaje se institucionaliza con el tiempo",
        findTrendsLede:
          "Proporción de páginas de cada año de rastreo que usan un "
          + "término. La señal más clara: el corpus pasa de «victim» "
          + "(víctima) a «survivor» (sobreviviente).",
        findRatio: "proporción survivor : victim (sobreviviente : víctima)",
        findRising: "En ascenso",
        findFalling: "En descenso",
        findKeynessTitle: "Dos colecciones, dos lenguajes de la memoria",
        findKeynessLede:
          "Log-verosimilitud de Dunning (G²): los términos "
          + "estadísticamente más distintivos de cada colección.",
        findDistinctiveOf: "distintivo de",
        findSourcesTitle: "Algunas memorias se sostienen en una sola fuente",
        findSourcesLede:
          "Proporción de las páginas de un término alojadas en su mayor "
          + "dominio de origen. Los términos sostenidos por un solo sitio "
          + "archivado son las memorias más frágiles del corpus.",
        findEffDomains: "dominios efect.",
        cat: {
          "Cultural practices": "Prácticas culturales",
          "Human rights activism": "Activismo de derechos humanos",
          "Transitional justice": "Justicia transicional",
          "Geography": "Geografía",
          "Social actors": "Actores sociales",
          "Institutions & services": "Instituciones y servicios",
          // September 2026 word sets
          "Human rights / activism": "Derechos humanos / activismo",
          "Regions": "Regiones",
          "Civil society actors": "Actores de la sociedad civil",
        },
        community: {
          "Commemoration & cultural practice":
            "Conmemoración y práctica cultural",
          "Advocacy infrastructure": "Infraestructura de incidencia",
          "Transitional justice": "Justicia transicional",
          "Places of activism": "Lugares de activismo",
          // September 2026 communities
          "Cultural practice and representation":
            "Práctica cultural y representación",
          "Struggle against impunity": "Lucha contra la impunidad",
          "Transitional justice and its institutions":
            "La justicia transicional y sus instituciones",
          "Post-Yugoslav memory": "Memoria posyugoslava",
          "Redress and apology": "Reparación y disculpa",
          // October 2026 consensus discourses (thematic sets only)
          "Transitional justice and redress":
            "Justicia transicional y reparación",
          "Documenting and exhibiting memory":
            "Documentar y exhibir la memoria",
          "Impunity, disappearance and resistance":
            "Impunidad, desaparición y resistencia",
          "Disappearance, armed conflict and resistance":
            "Desaparición, conflicto armado y resistencia",
          "The performing arts": "Las artes escénicas",
          "Indigenous culture and reconciliation":
            "Cultura indígena y reconciliación",
        },
        findCatShares:
          "proporción de páginas de cada año con al menos un término de "
          + "la categoría",
        findDiscoursesTitle: "{{count}} discursos estables que atraviesan las categorías",
        findPlaces: "Lugares",
        findLooser: "agrupación más laxa",
        findPeripheral: "vinculados de forma laxa",
        findPassages: "pasajes",
        findOneSite: "de un solo sitio",
        findDiscoursesLede:
          "Comunidades detectadas en la red de evidencia al ponderar las "
          + "relaciones por asociación estadística (PMI a nivel de "
          + "pasaje), conservadas solo cuando se repiten a lo largo de "
          + "cientos de ejecuciones con datos remuestreados; una agrupación "
          + "más laxa es aquella cuyos términos se agrupan, pero solo unos "
          + "pocos se mantienen unidos de forma fiable. Los lugares no forman parte "
          + "de la red: los países listados son aquellos cuyos pasajes "
          + "sobrerrepresentan un discurso, con su número de pasajes y una "
          + "advertencia cuando la mayoría procede de un solo sitio archivado.",
        findTiesTitle: "Los vínculos más fuertes",
        findTiesLede:
          "Los pares que comparten pasajes mucho más allá del azar (PMI): "
          + "emparejamientos precisos, no los mayores nodos.",
        findTiesShared: "pasajes compartidos",
        findTerms: "términos",
        lexSpine: "el conjunto central",
        lexSpineNote:
          "Las páginas entran al corpus a través de una lista de términos "
          + "ancla en prosa real, y solo se cuentan cuando un clasificador "
          + "validado juzga que tratan de los legados de violaciones "
          + "pasadas — el tema de este conjunto. Las demás categorías se "
          + "miden dentro de ese contexto.",
        lexMeasured:
          "medidas dentro del contexto de la justicia transicional",
        unlinkedNote:
          "{{count}} términos de esta colección aparecen en muy pocas "
          + "páginas para formar una relación (un vínculo requiere al menos "
          + "3 páginas compartidas) y no se dibujan:",
        noRelationships:
          "No se registran relaciones para este término en esta colección: "
          + "ningún otro término del léxico aparece en las mismas páginas "
          + "por encima del umbral de visualización.",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    lng: "en", // Default language

    keySeparator: ".",

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
  });

export default i18n;
